import {
    CommandWithFollowup,
    ILayout,
    ImageElement,
    LayoutElementType,
    LayoutsState,
    PictureStepData,
    Region,
    Step,
    VariantResource,
} from "../../types";
import { StepService } from "../../types";
import { WorkflowManager } from "../../WorkflowManager";
import { LayoutNotFoundError } from "../../util/exception";
import { AssetNotFoundError } from "../../util/exception";
import { CreateElementCommand, DeleteElementCommand, GroupCommand } from "../../command";
import { generate } from "../../util/guid";

class PictureStepService implements StepService<PictureStepData> {
    async init(
        stepData: Step<PictureStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) {
            return null;
        }
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            if (option.defaultVariant) {
                workflowManager.markStepsAsInitialised([stepData.stepName]);
                return this.selectVariantCommand(stepData, option.defaultVariant, workflowManager, () => {});
            }
        }
        workflowManager.markStepsAsInitialised([stepData.stepName]);
        return null;
    }

    private async reload(
        stepData: Step<PictureStepData>,
        workflowManager: WorkflowManager,
        reducerState: LayoutsState,
    ) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const option = stepData.option;
        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (option && variantId) {
                const variant = option.variants?.find((variant: VariantResource) => variant.id === variantId);
                if (variant) {
                    const regionElements = elementsForStep.map((el) => ({
                        id: el.id,
                        region: el.stepRegion,
                        regionIndex: el.stepRegionIndex,
                    }));
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [variant],
                        regionElements,
                        async () => {
                            workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                        },
                    );
                }
            }
        }
    }

    async selectVariant(
        stepData: Step<PictureStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setIsUpdating: (status: boolean) => void,
    ) {
        const result = await this.selectVariantCommand(stepData, variant, workflowManager, setIsUpdating);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async selectVariantCommand(
        stepData: Step<PictureStepData>,
        variant: VariantResource,
        workflowManager: WorkflowManager,
        setIsUpdating: (status: boolean) => void,
    ): Promise<CommandWithFollowup | null> {
        const asset = variant.asset;
        if (!asset) {
            throw new AssetNotFoundError(variant);
        }
        const link = asset?.fileLink;
        if (!link) {
            console.error("No URL for picture!");
            return null;
        }

        setIsUpdating(true);
        if (stepData.mandatory) {
            workflowManager.setMandatoryFulfilled(stepData.stepName, false);
        }

        // Remove existing images.
        const elements = workflowManager.getRegionElements(stepData.stepName);
        const deletionCommands = elements.map((elem) => new DeleteElementCommand(elem.id));

        // If images exists, delete them in preparation for creating new images.
        const createElementForRegion = (region: Region, regionIndex: number) => {
            const layouts = workflowManager.getLayouts();
            const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
            if (!layout) {
                throw new LayoutNotFoundError(region);
            }
            // Create a new image.
            const elementKey = generate();
            return {
                regionElement: { id: elementKey, region, regionIndex },
                command: new CreateElementCommand<ImageElement>(
                    {
                        stepName: stepData.stepName,
                        stepRegion: region,
                        stepRegionIndex: regionIndex,
                        id: elementKey,
                        src: link,
                        type: LayoutElementType.Image,
                        y: region.top,
                        x: region.left,
                        rotation: region.rotation,
                        width: region.width,
                        height: region.height,
                        layer: region.layer,
                        layerIndex: region.layerIndex,
                        immutable: region.immutable,
                        preserveAspectRatio: "none",
                    },
                    layout,
                ),
            };
        };

        const newElements = stepData.data.regions.map(createElementForRegion);
        return {
            command: new GroupCommand([...deletionCommands, ...newElements.map((el) => el.command)]),
            followup: async () => {
                await workflowManager.setSelectionsAndElements(
                    stepData.stepName,
                    [variant],
                    newElements.map((el) => el.regionElement),
                    async () => {
                        workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                        setIsUpdating(false);
                    },
                );
            },
        };
    }
}

export const pictureStepService = new PictureStepService();
